import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { SiteHeader } from '../components/SiteLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Calculator, ShieldCheck, FloppyDisk, Plus, Trash, ArrowsClockwise,
  CheckCircle, Warning, WarningCircle, Database,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* =========================================================================
 *  Centralized Exposure & MoS Workspace  (admin / assessor only)
 *
 *  Three panels:
 *   1) Scenario  — SCCS / IFRA product-category defaults  +  population band
 *   2) Overrides — amount g/day, frequency, retention, BW, route, dermal abs
 *   3) Ingredient table — auto-computed SED + MoS per row, with NOAEL pulled
 *      from the Aᴳ Masterlist (or overrideable inline for what-if testing)
 *
 *  Persistence: pick an existing formulation, then "Apply to Formulation"
 *  pushes the overrides onto its metadata.custom_exposure so the next
 *  Full Assessment / TRA-QRA PDF reflects them automatically.
 * =======================================================================*/

function MoSChip({ status, mos }) {
  if (mos == null) return <span className="text-xs text-[#6B7280]">—</span>;
  const map = {
    pass: 'bg-[#DEF7EC] text-[#03543F] border-[#31C48D]',
    borderline: 'bg-[#FEF3C7] text-[#723B13] border-[#F3CC64]',
    fail: 'bg-[#FDE8E8] text-[#9B1C1C] border-[#F8B4B4]',
  };
  const Icon = status === 'pass' ? CheckCircle : status === 'borderline' ? Warning : WarningCircle;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] font-bold tabular-nums ${map[status] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
      <Icon size={11} weight="fill" /> {mos.toLocaleString()}
    </span>
  );
}

function NumInput({ label, value, onChange, step = '0.01', placeholder, hint, testId }) {
  return (
    <div>
      <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#4B5563]">{label}</Label>
      <Input
        type="number" step={step} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
        className="mt-1 h-9 text-sm tabular-nums"
        data-testid={testId}
      />
      {hint && <p className="text-[10px] text-[#6B7280] mt-1">{hint}</p>}
    </div>
  );
}

export default function ExposurePage() {
  const [scenarios, setScenarios] = useState([]);
  const [populations, setPopulations] = useState([]);
  const [formulations, setFormulations] = useState([]);
  const [scenarioKey, setScenarioKey] = useState('face_cream');
  const [population, setPopulation] = useState('adult');
  const [overrides, setOverrides] = useState({
    amount_g: null, frequency: null, retention_factor: null,
    body_weight: null, ingestion_factor: null, exposure_route: null,
  });
  const [ingredients, setIngredients] = useState([
    { inci_name: 'Phenoxyethanol', concentration: 1, noael: null, dermal_absorption: null },
  ]);
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState('');

  // formulation pinning
  const [formulationId, setFormulationId] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  // bootstrap
  useEffect(() => {
    (async () => {
      try {
        const [s, p, f] = await Promise.all([
          axios.get(`${API_URL}/api/admin/exposure/scenarios`, { withCredentials: true }),
          axios.get(`${API_URL}/api/admin/exposure/populations`, { withCredentials: true }),
          axios.get(`${API_URL}/api/admin/exposure/formulations`, { withCredentials: true }),
        ]);
        setScenarios(s.data.scenarios || []);
        setPopulations(p.data.populations || []);
        setFormulations(f.data.formulations || []);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Failed to load reference data');
      }
    })();
  }, []);

  const selectedScenario = useMemo(
    () => scenarios.find(s => s.key === scenarioKey),
    [scenarios, scenarioKey],
  );
  const selectedPopulation = useMemo(
    () => populations.find(p => p.key === population),
    [populations, population],
  );

  // Group scenarios for picker
  const groupedScenarios = useMemo(() => {
    const groups = {};
    for (const s of scenarios) {
      const g = s.group || 'Other';
      if (!groups[g]) groups[g] = [];
      groups[g].push(s);
    }
    return groups;
  }, [scenarios]);

  const calculate = useCallback(async () => {
    setCalculating(true); setError(''); setResult(null);
    try {
      const cleanOverrides = {};
      for (const [k, v] of Object.entries(overrides)) {
        if (v !== null && v !== '' && !Number.isNaN(v)) cleanOverrides[k] = v;
      }
      const validIngredients = ingredients
        .filter(i => i.inci_name?.trim() && i.concentration > 0)
        .map(i => ({
          inci_name: i.inci_name.trim(),
          concentration: parseFloat(i.concentration),
          noael: i.noael !== null && i.noael !== '' ? parseFloat(i.noael) : null,
          dermal_absorption: i.dermal_absorption !== null && i.dermal_absorption !== '' ? parseFloat(i.dermal_absorption) : null,
        }));
      const r = await axios.post(`${API_URL}/api/admin/exposure/calculate`, {
        scenario_key: scenarioKey, population, overrides: cleanOverrides,
        ingredients: validIngredients,
      }, { withCredentials: true });
      setResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setCalculating(false);
    }
  }, [scenarioKey, population, overrides, ingredients]);

  // Recalculate on scenario / population change (with default overrides)
  useEffect(() => {
    if (scenarios.length && populations.length && ingredients.length > 0) {
      calculate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioKey, population, scenarios.length, populations.length]);

  const loadFormulation = async (id) => {
    if (!id) return;
    try {
      const r = await axios.get(`${API_URL}/api/admin/exposure/formulations/${id}`, { withCredentials: true });
      setFormulationId(id);
      setScenarioKey(r.data.product_category || 'custom');
      setPopulation(r.data.target_population || 'adult');
      const ce = r.data.custom_exposure || {};
      setOverrides({
        amount_g: ce.amount_g ?? null,
        frequency: ce.frequency ?? null,
        retention_factor: ce.retention_factor ?? null,
        body_weight: ce.body_weight ?? null,
        ingestion_factor: ce.ingestion_factor ?? null,
        exposure_route: ce.exposure_route ?? null,
      });
      setIngredients((r.data.ingredients || []).map(i => ({
        inci_name: i.inci_name, concentration: i.concentration,
        noael: null, dermal_absorption: null,
      })));
      setSaveMsg('');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load formulation');
    }
  };

  const saveOverride = async () => {
    if (!formulationId) return;
    setSaving(true); setSaveMsg('');
    try {
      const cleanOverrides = {};
      for (const [k, v] of Object.entries(overrides)) {
        if (v !== null && v !== '' && !Number.isNaN(v)) cleanOverrides[k] = v;
      }
      await axios.post(`${API_URL}/api/admin/exposure/formulations/${formulationId}/save`, {
        scenario_key: scenarioKey,
        population,
        overrides: cleanOverrides,
        notes,
      }, { withCredentials: true });
      setSaveMsg('Override applied. The next report for this formulation will use these values.');
    } catch (e) {
      setSaveMsg(`Failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const addIng = () => setIngredients(rs => [...rs, { inci_name: '', concentration: 0, noael: null, dermal_absorption: null }]);
  const setIng = (i, patch) => setIngredients(rs => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const removeIng = (i) => setIngredients(rs => rs.filter((_, idx) => idx !== i));

  const r = result?.resolved;

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <SiteHeader />
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <Calculator size={22} className="text-[#FF4E6A]" weight="duotone" />
            <h1 className="text-2xl font-bold tracking-tight text-[#111827]">Centralized Exposure &amp; MoS Engine</h1>
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563] bg-[#F3F4F6] px-2 py-0.5 rounded ml-2">internal · admin only</span>
          </div>
          <p className="text-sm text-[#4B5563]">
            SCCS / IFRA-aligned defaults, population scaling, and per-ingredient SED + MoS auto-calculation.
            Save overrides onto a formulation to feed the Full Assessment / TRA-QRA PDF engine.
          </p>
          <div className="flex gap-2 mt-3 flex-wrap">
            <Link to="/assessor"><Button variant="outline" className="h-8 text-xs rounded" data-testid="exposure-to-assessor">← Assessor Console</Button></Link>
            <Link to="/assessor/noael-extraction"><Button variant="outline" className="h-8 text-xs rounded" data-testid="exposure-to-noael">NOAEL Extraction →</Button></Link>
          </div>
        </div>

        {error && <div className="card-flat p-3 mb-4 text-sm text-[#9B1C1C] bg-[#FDE8E8] border border-[#F8B4B4]" data-testid="exposure-error">{error}</div>}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* LEFT: scenario + population + overrides */}
          <div className="card-flat p-5 lg:col-span-1 space-y-5">
            {/* Scenario */}
            <div>
              <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#111827] mb-2 flex items-center gap-1.5">
                <Database size={14} weight="duotone" /> Product Scenario
              </h2>
              <select
                value={scenarioKey}
                onChange={(e) => setScenarioKey(e.target.value)}
                className="w-full h-10 text-sm border border-gray-300 rounded px-2 bg-white"
                data-testid="exposure-scenario"
              >
                {Object.entries(groupedScenarios).map(([group, items]) => (
                  <optgroup key={group} label={group}>
                    {items.map(s => <option key={s.key} value={s.key}>{s.label}{s.subgroup ? ` · ${s.subgroup}` : ''}</option>)}
                  </optgroup>
                ))}
              </select>
              {selectedScenario && (
                <div className="mt-2 text-[11px] text-[#4B5563] grid grid-cols-2 gap-x-3 gap-y-1 tabular-nums">
                  <div>Default amount: <strong>{selectedScenario.amount_g} g/day</strong></div>
                  <div>Frequency: <strong>{selectedScenario.frequency}×/day</strong></div>
                  <div>Retention: <strong>{selectedScenario.retention_factor}</strong></div>
                  <div>Route: <strong className="uppercase">{selectedScenario.exposure_route}</strong></div>
                </div>
              )}
            </div>

            {/* Population */}
            <div>
              <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#111827] mb-2 flex items-center gap-1.5">
                <ShieldCheck size={14} weight="duotone" /> Target Population
              </h2>
              <select
                value={population}
                onChange={(e) => setPopulation(e.target.value)}
                className="w-full h-10 text-sm border border-gray-300 rounded px-2 bg-white"
                data-testid="exposure-population"
              >
                {populations.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
              {selectedPopulation && (
                <div className="mt-2 text-[11px] text-[#4B5563] tabular-nums">
                  Body weight: <strong>{selectedPopulation.body_weight} kg</strong> ·
                  BSA factor: <strong>{selectedPopulation.bsa_factor}×</strong> ·
                  Conservatism: <strong className="uppercase">{selectedPopulation.conservatism}</strong>
                </div>
              )}
            </div>

            {/* Overrides */}
            <div>
              <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#111827] mb-2">
                Overrides (optional)
              </h2>
              <div className="grid grid-cols-2 gap-3">
                <NumInput label="Amount g/day" value={overrides.amount_g ?? ''} placeholder={selectedScenario?.amount_g} onChange={v => setOverrides(o => ({ ...o, amount_g: v }))} testId="ov-amount-g" />
                <NumInput label="Frequency /day" value={overrides.frequency ?? ''} placeholder={selectedScenario?.frequency} onChange={v => setOverrides(o => ({ ...o, frequency: v }))} testId="ov-frequency" />
                <NumInput label="Retention" value={overrides.retention_factor ?? ''} step="0.01" placeholder={selectedScenario?.retention_factor} onChange={v => setOverrides(o => ({ ...o, retention_factor: v }))} testId="ov-retention" />
                <NumInput label="Body weight kg" value={overrides.body_weight ?? ''} placeholder={selectedPopulation?.body_weight} onChange={v => setOverrides(o => ({ ...o, body_weight: v }))} testId="ov-bw" />
                <NumInput label="Ingestion factor" value={overrides.ingestion_factor ?? ''} placeholder={selectedScenario?.ingestion_factor ?? 0} onChange={v => setOverrides(o => ({ ...o, ingestion_factor: v }))} testId="ov-ingestion" />
                <div>
                  <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#4B5563]">Route</Label>
                  <select
                    value={overrides.exposure_route ?? ''}
                    onChange={(e) => setOverrides(o => ({ ...o, exposure_route: e.target.value || null }))}
                    className="mt-1 w-full h-9 text-sm border border-gray-300 rounded px-2 bg-white"
                    data-testid="ov-route"
                  >
                    <option value="">(scenario default)</option>
                    <option value="dermal">Dermal</option>
                    <option value="oral">Oral</option>
                    <option value="inhalation">Inhalation</option>
                  </select>
                </div>
              </div>
            </div>

            <Button onClick={calculate} disabled={calculating} className="w-full h-10 rounded bg-[#0B1426] text-white hover:bg-[#1B2538] text-sm font-semibold" data-testid="exposure-recalculate">
              <ArrowsClockwise size={14} className="mr-1.5" weight="bold" />
              {calculating ? 'Calculating…' : 'Recalculate'}
            </Button>
          </div>

          {/* RIGHT: ingredients + results */}
          <div className="lg:col-span-2 space-y-4">
            {/* Resolved-params banner */}
            {r && (
              <div className="card-flat p-4 bg-white" data-testid="resolved-banner">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#FF4E6A]">Applied exposure</p>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">{r.is_custom_override ? 'override active' : 'category defaults'}</p>
                </div>
                <p className="text-sm font-semibold text-[#111827] mb-2">
                  {r.applied_label} <span className="text-[#4B5563] font-normal">· {r.population_label}</span>
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] tabular-nums">
                  <div className="bg-[#F9FAFB] p-2 rounded"><span className="text-[#6B7280]">Amount</span><br/><strong>{r.amount_g} g/day</strong></div>
                  <div className="bg-[#F9FAFB] p-2 rounded"><span className="text-[#6B7280]">Freq</span><br/><strong>{r.frequency} /day</strong></div>
                  <div className="bg-[#F9FAFB] p-2 rounded"><span className="text-[#6B7280]">Retention</span><br/><strong>{r.retention_factor}</strong></div>
                  <div className="bg-[#F9FAFB] p-2 rounded"><span className="text-[#6B7280]">BW</span><br/><strong>{r.body_weight} kg</strong></div>
                  <div className="bg-[#F9FAFB] p-2 rounded"><span className="text-[#6B7280]">Route</span><br/><strong className="uppercase">{r.exposure_route}</strong></div>
                  <div className="bg-[#F9FAFB] p-2 rounded"><span className="text-[#6B7280]">Ingestion</span><br/><strong>{r.ingestion_factor ?? 0}</strong></div>
                  <div className="bg-[#F9FAFB] p-2 rounded"><span className="text-[#6B7280]">MoS thresh</span><br/><strong>{r.mos_threshold}</strong></div>
                  <div className="bg-[#F9FAFB] p-2 rounded"><span className="text-[#6B7280]">Conservatism</span><br/><strong className="uppercase">{r.conservatism}</strong></div>
                </div>
              </div>
            )}

            {/* Ingredient table */}
            <div className="card-flat p-4 bg-white">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#111827]">Ingredients · SED &amp; MoS</h2>
                <Button onClick={addIng} variant="outline" className="h-7 text-xs rounded" data-testid="add-ingredient"><Plus size={12} className="mr-1" weight="bold" /> Add</Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[12px]">
                  <thead className="bg-gray-50">
                    <tr className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">
                      <th className="text-left p-2">INCI</th>
                      <th className="text-right p-2 w-20">Conc %</th>
                      <th className="text-right p-2 w-24">NOAEL <span className="font-normal normal-case">(mg/kg)</span></th>
                      <th className="text-right p-2 w-20">Derm Abs %</th>
                      <th className="text-right p-2 w-28">SED (mg/kg/d)</th>
                      <th className="text-right p-2 w-24">MoS</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {ingredients.map((ing, i) => {
                      const row = result?.rows?.[i];
                      return (
                        <tr key={i} className="border-t border-gray-100 align-top">
                          <td className="p-1.5"><Input value={ing.inci_name} onChange={e => setIng(i, { inci_name: e.target.value })} className="h-8 text-sm" placeholder="e.g. Phenoxyethanol" data-testid={`ing-name-${i}`} /></td>
                          <td className="p-1.5"><Input type="number" step="0.01" value={ing.concentration} onChange={e => setIng(i, { concentration: e.target.value })} className="h-8 text-sm text-right tabular-nums" data-testid={`ing-conc-${i}`} /></td>
                          <td className="p-1.5"><Input type="number" step="0.1" value={ing.noael ?? ''} placeholder={row?.noael ?? 'auto'} onChange={e => setIng(i, { noael: e.target.value })} className="h-8 text-sm text-right tabular-nums" data-testid={`ing-noael-${i}`} /></td>
                          <td className="p-1.5"><Input type="number" step="1" value={ing.dermal_absorption ?? ''} placeholder={row?.dermal_absorption ?? '100'} onChange={e => setIng(i, { dermal_absorption: e.target.value })} className="h-8 text-sm text-right tabular-nums" data-testid={`ing-derm-${i}`} /></td>
                          <td className="p-1.5 text-right tabular-nums text-[#111827] font-semibold" data-testid={`ing-sed-${i}`}>{row?.sed != null ? row.sed.toFixed(4) : '—'}</td>
                          <td className="p-1.5 text-right" data-testid={`ing-mos-${i}`}><MoSChip status={row?.mos_status} mos={row?.mos} /></td>
                          <td className="p-1.5"><button onClick={() => removeIng(i)} className="text-[#9B1C1C] hover:text-[#7B1313]" data-testid={`ing-remove-${i}`}><Trash size={14} /></button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {ingredients.length === 0 && <p className="text-center text-xs text-[#6B7280] py-6">No ingredients. Click <em>Add</em> to start.</p>}
              </div>
              {result?.rows && (
                <p className="text-[10px] text-[#6B7280] mt-2">
                  NOAEL and dermal absorption pull from the Aᴳ Masterlist when blank.
                  <span className="ml-2">MoS thresholds: <strong>&lt;100</strong> fail · <strong>100–200</strong> borderline · <strong>≥200</strong> pass.</span>
                </p>
              )}
            </div>

            {/* Apply to formulation */}
            <div className="card-flat p-4 bg-white">
              <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#111827] mb-3">Apply to formulation</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                <div className="sm:col-span-2">
                  <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#4B5563]">Select formulation</Label>
                  <select
                    value={formulationId}
                    onChange={(e) => loadFormulation(e.target.value)}
                    className="mt-1 w-full h-9 text-sm border border-gray-300 rounded px-2 bg-white"
                    data-testid="formulation-picker"
                  >
                    <option value="">— pick a recent formulation to override —</option>
                    {formulations.map(f => (
                      <option key={f.id} value={f.id}>
                        {f.product_name || 'Untitled'} · {f.assessment_mode} {f.has_override ? '· (override set)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={saveOverride}
                    disabled={!formulationId || saving}
                    className="w-full h-9 rounded bg-[#FF4E6A] hover:bg-[#E63E5A] text-white text-sm font-semibold"
                    data-testid="save-override"
                  >
                    <FloppyDisk size={14} className="mr-1.5" weight="bold" />
                    {saving ? 'Saving…' : 'Apply Override'}
                  </Button>
                </div>
              </div>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional rationale (e.g. 'Override dermal abs based on supplier-provided HPLC data; retention 0.1 for rinse-off scenario.')"
                className="text-sm"
                rows={2}
                data-testid="override-notes"
              />
              {saveMsg && <p className={`text-[11px] mt-2 ${saveMsg.startsWith('Failed') ? 'text-[#9B1C1C]' : 'text-[#03543F]'}`} data-testid="save-message">{saveMsg}</p>}
              <p className="text-[10px] text-[#6B7280] mt-2">Override applies to the formulation's next regenerated report. The audit log retains every prior set of overrides with assessor name and timestamp.</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
